import {logErrorToSentry} from "./errors/logErrorToSentry";

export const loadGoogleTagManager = () =>  {
    return new Promise<void>((resolve, reject) => {
        const gtmId = import.meta.env.VITE_GTM;
        if (!window.google_tag_manager || !window.google_tag_manager[gtmId]) {
            (function (w: any, d: any, s: any, l: any, i: any) {
                w[l] = w[l] || [];
                w[l].push({
                    'gtm.start': new Date().getTime(),
                    event: 'gtm.js'
                });
                var f = d.getElementsByTagName(s)[0],
                    script = d.createElement(s), dl = l != 'dataLayer' ? '&l=' + l : '';
                script.async = true;
                script.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;

                script.onload = () => resolve();
                script.onerror = (error: any) => reject(new Error('Failed to load GTM script.'));

                f.parentNode.insertBefore(script, f);
            })(window, document, 'script', 'dataLayer', gtmId);
        } else {
            resolve();
        }
    });
}


export const GA_EVENTS = {
    SIGN_UP: 'Signup_APP',
    SIGN_UP_FAIL: 'Signup_APP_FAIL',
    FEATURE_FIRST_USED: 'feature_first_used',
    TRIAL_UPGRADE_INTENT: 'trial_upgrade_intent',
    LIMIT_HIT: 'limit_hit',
} as const;

export type GA_EVENT = typeof GA_EVENTS[keyof typeof GA_EVENTS];

export const sendGAEvent = async (gaEvent: GA_EVENT ,userId?: string) => {
    try {
        await loadGoogleTagManager();
        const gaData: any = {
            event: gaEvent
        };

        if (userId) {
            gaData.userId = userId;
        }

        window.dataLayer.push(gaData);
    } catch(error: any) {
        logErrorToSentry(error)
    }
}

export const pushEvent = (name: string, params?: Record<string, any>) => {
    if (typeof window === 'undefined' || !window.dataLayer) return;
    window.dataLayer.push({ event: name, ...params });
};

const FIRST_USED_KEY = 'lgc_first_used';

export const recordFeatureFirstUsed = (
    feature: 'documents' | 'quizzes' | 'tasks' | 'ai',
    clientId: string
) => {
    let used: string[] = [];
    try {
        used = JSON.parse(localStorage.getItem(FIRST_USED_KEY) ?? '[]');
    } catch {}
    if (used.includes(feature)) return;
    used.push(feature);
    try {
        localStorage.setItem(FIRST_USED_KEY, JSON.stringify(used));
    } catch {}
    pushEvent(GA_EVENTS.FEATURE_FIRST_USED, { feature, client_id: clientId });
};

export const recordUpgradeIntent = (userId?: string) => {
    pushEvent(GA_EVENTS.TRIAL_UPGRADE_INTENT, userId ? { user_id: userId } : undefined);
};

export const recordLimitHit = (limitType: 'location' | 'user' | 'storage') => {
    pushEvent(GA_EVENTS.LIMIT_HIT, { limit_type: limitType });
};

// ── Demo mode analytics ──────────────────────────────────────────────────────

export const DEMO_GA_EVENTS = {
    SESSION_START: 'demo_session_start',
    FIRST_ACTION: 'demo_first_action',
    ACTION: 'demo_action',
    MILESTONE: 'demo_milestone',
    SIGNUP_INTENT: 'demo_signup_intent',
    RESET: 'demo_reset',
} as const;

type DemoAction =
    | 'create_item'
    | 'open_doc'
    | 'rename_item'
    | 'trash_item'
    | 'create_workspace'
    | 'open_settings'
    | 'switch_scenario';

type DemoMilestone = '30s' | '2m' | '5m';

let _demoSessionStart: number | null = null;
let _demoFirstActionFired = false;
let _demoActionsCount = 0;
const _demoMilestonesFired = new Set<DemoMilestone>();
const MILESTONE_MS: Record<DemoMilestone, number> = {'30s': 30_000, '2m': 120_000, '5m': 300_000};

function _checkMilestones() {
    if (!_demoSessionStart) return;
    const elapsed = Date.now() - _demoSessionStart;
    for (const [milestone, ms] of Object.entries(MILESTONE_MS) as [DemoMilestone, number][]) {
        if (elapsed >= ms && !_demoMilestonesFired.has(milestone)) {
            _demoMilestonesFired.add(milestone);
            pushEvent(DEMO_GA_EVENTS.MILESTONE, {
                milestone,
                actions_taken: _demoActionsCount,
                scenario: localStorage.getItem('lgc-demo-scenario') ?? 'saas-onboarding',
            });
        }
    }
}

let _milestoneInterval: ReturnType<typeof setInterval> | null = null;

export function trackDemoSessionStart(scenario: string) {
    _demoSessionStart = Date.now();
    pushEvent(DEMO_GA_EVENTS.SESSION_START, {
        scenario,
        referrer: document.referrer || 'direct',
    });
    _milestoneInterval = setInterval(_checkMilestones, 10_000);
}

export function trackDemoAction(action: DemoAction) {
    _demoActionsCount++;
    pushEvent(DEMO_GA_EVENTS.ACTION, {
        action,
        scenario: localStorage.getItem('lgc-demo-scenario') ?? 'saas-onboarding',
    });
    if (!_demoFirstActionFired) {
        _demoFirstActionFired = true;
        const secondsToFirst = _demoSessionStart
            ? Math.round((Date.now() - _demoSessionStart) / 1000)
            : 0;
        pushEvent(DEMO_GA_EVENTS.FIRST_ACTION, {
            action_type: action,
            scenario: localStorage.getItem('lgc-demo-scenario') ?? 'saas-onboarding',
            seconds_to_first_action: secondsToFirst,
        });
    }
    _checkMilestones();
}

export function trackDemoSignupIntent() {
    pushEvent(DEMO_GA_EVENTS.SIGNUP_INTENT, {
        scenario: localStorage.getItem('lgc-demo-scenario') ?? 'saas-onboarding',
        time_in_demo: _demoSessionStart ? Math.round((Date.now() - _demoSessionStart) / 1000) : 0,
        actions_taken: _demoActionsCount,
    });
}

export function trackDemoReset(scenario: string) {
    pushEvent(DEMO_GA_EVENTS.RESET, {
        scenario,
        time_in_demo: _demoSessionStart ? Math.round((Date.now() - _demoSessionStart) / 1000) : 0,
    });
}

export const getUTMFromCookie = (name: string = 'utm_data') => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        return JSON.parse(decodeURIComponent(parts.pop()!.split(';').shift()!));
    }
    return null;
}
